// A seed's Audiotool page (/s/:id/audiotool): the projects on YOUR Audiotool
// account that this song has been through, and what to do with each.
//
// The link lives on the projects themselves (shared/audiotool/link.ts): every
// project a seed is opened into or imported from carries the song's tag, so
// this page asks Audiotool rather than any Seedbed table, and finds the same
// projects from any browser — or from Live.
//
//   • a linked project → open it in the studio, or import it back as a new
//     version (your seed) or a remix (someone else's);
//   • none yet, or want a fresh one → the export page makes a new project;
//   • a project that isn't linked → paste its link.

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronLeft, ExternalLink, Loader2, Plus, Upload } from "lucide-react";
import type { SeedApiDetail } from "@shared/api-types.js";
import { projectNameFromUrl } from "@shared/audiotool/link.js";
import { findLinkedProjects, type LinkedProject } from "@audiotool-core/link.js";
import { Button } from "@web-shared/components/ui/button";
import { Skeleton } from "@web-shared/components/ui/skeleton";
import { useAudiotool } from "@web-shared/hooks/useAudiotool";
import { webApi } from "@web-shared/lib/web-api";
import { relativeTime } from "@web-shared/lib/format";
import { audiotoolExportPath, audiotoolUploadPath, seedPath } from "@/lib/ctas";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useSession } from "../session";
import { AmbientGlow } from "@/components/AmbientGlow";

export function AudiotoolHubPage() {
  const { id = "" } = useParams();
  useDocumentTitle("Audiotool");
  const session = useSession();
  const seed = useQuery({
    queryKey: ["web", "seed", id, session?.user.id ?? ""],
    queryFn: () => webApi<SeedApiDetail>(`/seeds/${encodeURIComponent(id)}`),
  });
  const at = useAudiotoolSignIn(false);

  const rootSeedId = seed.data?.rootSeedId || seed.data?.id || "";
  const linked = useQuery({
    queryKey: ["audiotool", "linked", rootSeedId, at.status === "signed-in" ? at.userName : ""],
    enabled: at.status === "signed-in" && rootSeedId !== "",
    queryFn: () => findLinkedProjects(at.status === "signed-in" ? at.client : never(), rootSeedId),
    staleTime: 30_000,
  });

  const mine = Boolean(session && seed.data && session.user.id === seed.data.producer.id);
  const importLabel = mine ? "Import as new version" : "Import as a remix";

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <AmbientGlow />

      <Link to={seedPath(id)} className="relative z-10 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ChevronLeft className="size-3.5" /> back to seed
      </Link>

      {/* Header with Audiotool icon */}
      <div className="flex items-center gap-4">
        <div className="size-12 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
          <img src="/audiotool-icon.webp" alt="" className="size-8 rounded" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl font-black tracking-tight">Audiotool Hub</h1>
          <p className="text-sm text-muted-foreground truncate">
            {seed.data ? seed.data.title : <Skeleton className="h-4 w-32 inline-block" />}
            {at.status === "signed-in" && <span className="text-muted-foreground/50"> · {at.userName}</span>}
          </p>
        </div>
      </div>

      {seed.isError && <p className="text-sm text-removed">Couldn't load this seed.</p>}

      {/* Step 1: Connect Audiotool */}
      {at.status !== "signed-in" && (
        <AudiotoolStatus
          at={at}
          connect="Connect your Audiotool account to export this seed as a project, or import one back."
        />
      )}

      {/* Connected: show projects + actions */}
      {at.status === "signed-in" && (
        <>
          {/* Primary action: start a new project */}
          <section className="rounded-2xl border border-primary/20 p-4" style={{ background: "linear-gradient(135deg, #1a1528, #111116)" }}>
            <h2 className="text-sm font-bold">New project from this seed</h2>
            <Button asChild className="mt-3 w-full bg-[#7c5cbf] hover:bg-[#9171d4]">
              <Link to={audiotoolExportPath(id)}>
                <Plus className="size-4" /> Open in Audiotool
              </Link>
            </Button>
          </section>

          {/* Linked projects — collapsible */}
          <CollapsibleSection title="Your Audiotool projects linked to this song">
            {linked.isPending && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" /> Scanning your projects...
              </p>
            )}
            {linked.isError && <p className="text-sm text-removed">{linked.error.message}</p>}
            {linked.data?.length === 0 && (
              <p className="text-xs text-muted-foreground">No projects linked to this song yet.</p>
            )}
            {linked.data && linked.data.length > 0 && (
              <ul className="divide-y divide-hairline">
                {linked.data.map((p) => (
                  <LinkedRow key={p.name} project={p} seedId={id} currentSeedId={seed.data?.id ?? id} importLabel={importLabel} />
                ))}
              </ul>
            )}
          </CollapsibleSection>

          {/* Paste a link — collapsible */}
          <CollapsibleSection title="Import new Audiotool project">
            <PasteLink parent={id} label={importLabel} />
          </CollapsibleSection>
        </>
      )}
    </div>
  );
}

function CollapsibleSection({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-2xl border border-hairline bg-raised overflow-hidden">
      <button
        type="button"
        className="flex w-full items-center justify-between p-4 text-sm font-bold hover:bg-white/[0.03] transition-colors"
        onClick={() => setOpen((o) => !o)}
      >
        {title}
        <ChevronDown className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="border-t border-hairline p-4 pt-3 space-y-3">{children}</div>}
    </section>
  );
}

function LinkedRow({
  project,
  seedId,
  currentSeedId,
  importLabel,
}: {
  project: LinkedProject;
  seedId: string;
  currentSeedId: string;
  importLabel: string;
}) {
  const synced = project.link.seedId === currentSeedId;
  return (
    <li className="flex items-center gap-3 py-3">
      <div className="size-9 rounded-lg bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
        <img src="/audiotool-icon.webp" alt="" className="size-5 rounded" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{project.displayName}</p>
        <p className="truncate text-[11px] text-muted-foreground">
          {project.updated ? relativeTime(project.updated.toISOString()) : "never edited"}
          {!synced && " · different version"}
        </p>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        <Button asChild size="sm" variant="ghost" className="h-8 px-2">
          <a href={project.studioUrl} target="_blank" rel="noreferrer" title="Open in Audiotool Studio">
            <ExternalLink className="size-3.5" />
          </a>
        </Button>
        <Button asChild size="sm" variant="outline" className="h-8">
          <Link to={audiotoolUploadPath({ project: project.name, parent: seedId })}>
            <Upload className="size-3.5" /> {importLabel}
          </Link>
        </Button>
      </div>
    </li>
  );
}

/** A project that isn't linked yet: paste its link, import it under `parent`. */
export function PasteLink({ parent, label }: { parent?: string; label: string }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const name = projectNameFromUrl(text);
  return (
    <form
      className="flex gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (name) navigate(audiotoolUploadPath({ project: name, ...(parent ? { parent } : {}) }));
      }}
    >
      <input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste an Audiotool project link"
        aria-label="Audiotool project link"
        className="min-w-0 flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm"
      />
      <Button type="submit" variant="outline" disabled={!name}>
        {label}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// The Audiotool session, signed in on arrival
// ---------------------------------------------------------------------------

export type AudiotoolState = ReturnType<typeof useAudiotool>["session"];

/**
 * The Audiotool session — and, where landing on the page IS the intent (the
 * export page: the project is already being built), off to Audiotool once to
 * get it. A sign-in that came back with an error is shown, not retried.
 *
 * `auto` is false on the hub, which is a page someone READS: it lists the
 * projects this song has been through and what to do with each. Bouncing a
 * visitor to a third party's sign-in before they have seen any of that loses
 * the ones who have no Audiotool account, which is most of them.
 */
export function useAudiotoolSignIn(auto = true): AudiotoolState {
  const { session, signIn } = useAudiotool();
  const tried = useRef(false);
  useEffect(() => {
    if (!auto || session.status !== "signed-out" || session.error || tried.current) return;
    tried.current = true;
    signIn(window.location.pathname + window.location.search);
  }, [auto, session, signIn]);
  return session;
}

export function AudiotoolStatus({ at, connect }: { at: AudiotoolState; connect?: string }) {
  const { signIn, redirectUrl } = useAudiotool();
  const goSignIn = () => signIn(window.location.pathname + window.location.search);
  if (at.status === "checking") {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Connecting to Audiotool…
      </p>
    );
  }
  if (at.status === "signed-out" && !at.error) {
    // Nothing has gone wrong — this page just hasn't asked yet (`connect`), or
    // it has and Audiotool is about to take over the tab.
    if (!connect) {
      return (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Going to Audiotool to sign in…
        </p>
      );
    }
    return (
      <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-strong p-4">
        <p className="text-sm text-muted-foreground">{connect}</p>
        <Button size="sm" onClick={goSignIn}>
          Connect Audiotool
        </Button>
      </div>
    );
  }
  if (at.status === "signed-out") {
    return (
      <div className="space-y-2 rounded-2xl border border-dashed border-strong p-4 text-sm">
        <p className="text-removed">Audiotool sign-in: {at.error}</p>
        <Button size="sm" variant="outline" onClick={goSignIn}>
          Try again
        </Button>
        <p className="text-xs text-muted-foreground">
          If Audiotool says the redirect_uri doesn't match, <code>{redirectUrl}</code> needs adding to the Seedbed app's
          redirect URIs at developer.audiotool.com.
        </p>
      </div>
    );
  }
  return null;
}

function never(): never {
  throw new Error("Not signed in to Audiotool.");
}
